import { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { toast } from "react-toastify";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import {
  ArrowLongRightIcon,
  CloseIcon,
  DownloadIcon,
  PlusIcon,
} from "../../assets/icons";
import itemImage from "../../assets/images/inventory-item.png";
import {
  Badge,
  Button,
  DataRow,
  ItemFilters,
  Modal,
  PageTitle,
  Section,
  Loading,
} from "../../components";
import { TextInput, NumberInput } from "../../components/forms";
import { enums } from "../../constants/enum";
import useItemService from "../../services/api/itemService";
import usePurchaseService from "../../services/api/purchaseService";
import { purchaseCreateSchema } from "../../schemas/purchase";
import currencyFormatter from "../../util/currencyFormatter";
import "./PurchasesPage.scss";

function AddPurchasePage() {
  const navigate = useNavigate();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewedItem, setViewedItem] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    category: enums.ITEM_FILTER_CATEGORIES.ALL,
    stockStatus: enums.ITEM_FILTER_STOCK_STATUS.ALL,
    sort: enums.ITEM_SORTING_OPTIONS.NEWEST_FIRST,
    minPrice: "",
    maxPrice: "",
  });
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Ref to access General Info form values
  const generalInfoFormRef = useRef(null);

  const { ref, inView } = useInView();
  const { getAllItems, queryKey: itemQueryKey } = useItemService();
  const { addNewPurchase, queryKey: purchaseQueryKey } = usePurchaseService();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: addNewPurchase,
    onSuccess: async () => {
      toast.success("Purchase added successfully");
      await queryClient.resetQueries([purchaseQueryKey]);
      navigate("/purchases");
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.payload?.message || "Failed to add purchase",
      );
    },
  });

  const {
    data,
    isLoading: isItemsLoading,
    error: itemsError,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [itemQueryKey, filters],
    queryFn: ({ pageParam = 1 }) =>
      getAllItems({ ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage?.totalPages > lastPage?.currentPage) {
        return lastPage?.currentPage + 1;
      }
      return undefined;
    },
    enabled: isSearchActive,
  });

  useEffect(() => {
    if (inView && isSearchActive) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, isSearchActive]);

  useEffect(() => {
    if (itemsError) {
      toast.error(
        itemsError?.response?.data?.payload?.message ||
          enums.DEFAULT_ERROR_MESSAGE,
      );
    }
  }, [itemsError]);

  const handleFilterSubmit = (values) => {
    setFilters(values);
    setIsSearchActive(true);
  };

  const addItemToSelectedItems = (item) => {
    setSelectedItems((prev) => {
      if (prev.some((i) => i._id === item._id)) return prev;
      return [...prev, { ...item, purchaseQuantity: 1 }];
    });
  };

  const removeItemFromSelectedItems = (itemId) => {
    setSelectedItems((prev) => prev.filter((i) => i._id !== itemId));
  };

  const handleQuantityChange = (itemId, quantity) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item._id === itemId ? { ...item, purchaseQuantity: quantity } : item,
      ),
    );
  };

  const handleViewItem = (item) => {
    setViewedItem(item);
    setShowDetailsModal(true);
  };

  const calculateTotal = () => {
    return selectedItems.reduce(
      (total, item) =>
        total + (item.sellingPrice || 0) * (item.purchaseQuantity || 1),
      0,
    );
  };

  const handleSubmit = async () => {
    if (!generalInfoFormRef.current) return;

    const { values, validateForm } = generalInfoFormRef.current;

    // Trigger validation
    const formErrors = await validateForm();

    if (Object.keys(formErrors).length > 0) {
      // Mark fields as touched to show errors
      generalInfoFormRef.current.setTouched({
        billNumber: true,
        purchaseDate: true,
      });
      return;
    }

    const payload = {
      billNumber: values.billNumber,
      items: selectedItems.map((item) => ({
        item: item._id,
        quantity: Number(item.purchaseQuantity),
      })),
    };

    try {
      await purchaseCreateSchema.validate(payload, { abortEarly: false });
      mutation.mutate({ payload });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          toast.error(error.message);
        });
      } else {
        toast.error("Validation failed");
      }
    }
  };

  const makeBadgeVariant = useCallback((itemCategory) => {
    switch (itemCategory) {
      case enums.ITEM_CATEGORIES.FRAME:
        return "info";
      case enums.ITEM_CATEGORIES.LENSE:
        return "success";
      case enums.ITEM_CATEGORIES.ACCESSORIES:
        return "warning";
      case enums.ITEM_CATEGORIES.SUNGLASS:
        return "danger";
      default:
        return "info";
    }
  }, []);

  return (
    <>
      <div className="page add-purchase-page">
        <PageTitle text="New Purchase" prev="/purchases" />

        <Section title="General Information">
          <Formik
            innerRef={generalInfoFormRef}
            validationSchema={purchaseCreateSchema}
            initialValues={{
              billNumber: "",
            }}
            onSubmit={() => {}}
          >
            <Form>
              <div className="input-group">
                <TextInput
                  label="Bill Number"
                  placeholder="Type here"
                  name="billNumber"
                />
                {/* <DateInput label="Purchase Date" name="purchaseDate" /> */}
              </div>
            </Form>
          </Formik>
        </Section>

        <Section title="Select Items">
          <ItemFilters
            initialValues={filters}
            onSubmit={handleFilterSubmit}
            onClear={() => {
              setFilters({
                search: "",
                category: enums.ITEM_FILTER_CATEGORIES.ALL,
                stockStatus: enums.ITEM_FILTER_STOCK_STATUS.ALL,
                sort: enums.ITEM_SORTING_OPTIONS.NEWEST_FIRST,
                minPrice: "",
                maxPrice: "",
              });
              setIsSearchActive(false);
            }}
          />

          <div className="inventory-list">
            {isItemsLoading ? (
              <Loading variant="inline" />
            ) : (
              data?.pages?.map((page) =>
                page.items?.map((item) => (
                  <div className="item-card" key={item._id}>
                    <div className="item-img">
                      <div className="img-bx">
                        <img src={itemImage} alt="Item" />
                      </div>
                    </div>

                    <div className="item-meta">
                      <div className="item-name">
                        <i className="item-brand-letter">
                          {item.brand?.[0] || "I"}
                        </i>
                        <p>{item.itemName}</p>
                      </div>

                      <div className="item-stats">
                        <div>
                          <Badge
                            className="badge"
                            text={item.category}
                            variant={makeBadgeVariant(item.category)}
                            outlined
                          />
                          <div className="separator"></div>
                          <Badge
                            className="badge"
                            text={item.inventory?.stockStatus || "N/A"}
                          />
                        </div>

                        <div className="separator"></div>

                        <div>
                          <p>
                            Storeroom Stock:{" "}
                            {item.inventory?.storeRoomStock || 0}
                          </p>
                          <div className="separator"></div>
                          <p>
                            Showcase Stock: {item.inventory?.displayStock || 0}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="item-price">
                      <span>Selling Price</span>
                      <p>
                        {currencyFormatter(item.sellingPrice)}{" "}
                        <small>LKR</small>
                      </p>
                    </div>

                    <div className="item-actions">
                      <Button
                        prefixIcon={<PlusIcon />}
                        text="Add"
                        onClick={() => addItemToSelectedItems(item)}
                        disabled={selectedItems.some((i) => i._id === item._id)}
                      />
                      <Button
                        text="View"
                        prefixIcon={<ArrowLongRightIcon />}
                        onClick={() => handleViewItem(item)}
                      />
                    </div>
                  </div>
                )),
              )
            )}
            <div ref={ref} className="loading-trigger">
              {isFetchingNextPage && <Loading variant="inline" />}
            </div>
            {isSearchActive &&
              !isItemsLoading &&
              (!data?.pages || data?.pages?.[0]?.items?.length === 0) && (
                <div
                  className="no-data"
                  style={{ padding: "20px", textAlign: "center" }}
                >
                  No items found.
                </div>
              )}
          </div>
        </Section>

        {selectedItems.length > 0 && (
          <Section title="Selected Items">
            <div className="inventory-list">
              {selectedItems.map((item) => (
                <div className="item-card" key={item._id}>
                  <div className="item-img">
                    <div className="img-bx">
                      <img src={itemImage} alt="Item" />
                    </div>
                  </div>

                  <div className="item-meta">
                    <div className="item-name">
                      <i className="item-brand-letter">
                        {item.brand?.[0] || "I"}
                      </i>
                      <p>{item.itemName}</p>
                    </div>

                    <div className="item-stats">
                      <div>
                        <Badge
                          className="badge"
                          text={item.category}
                          variant={makeBadgeVariant(item.category)}
                          outlined
                        />
                      </div>
                    </div>
                  </div>

                  <div className="item-price">
                    <span>Quantity</span>
                    <div style={{ width: "100px" }}>
                      <NumberInput
                        value={item.purchaseQuantity}
                        onChange={(val) => handleQuantityChange(item._id, val)}
                        min={1}
                      />
                    </div>
                  </div>

                  <div className="item-price">
                    <span>Total</span>
                    <p>
                      {currencyFormatter(
                        (item.sellingPrice || 0) * (item.purchaseQuantity || 1),
                      )}{" "}
                      <small>LKR</small>
                    </p>
                  </div>

                  <div className="item-actions">
                    <Button
                      prefixIcon={<CloseIcon />}
                      text="Remove"
                      onClick={() => removeItemFromSelectedItems(item._id)}
                    />
                    <Button
                      text="View"
                      prefixIcon={<ArrowLongRightIcon />}
                      onClick={() => handleViewItem(item)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        <DataRow
          className="total-section"
          labelLeft="Total"
          dataLeft={
            <p className="total-price">
              {currencyFormatter(calculateTotal())} LKR
            </p>
          }
          fluid
        />

        <Button
          text="Submit"
          icon={<DownloadIcon />}
          onClick={handleSubmit}
          disabled={mutation.isPending}
        />
      </div>

      <Modal
        className="inventory-item-details-modal"
        title="Item Details"
        close={!showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
      >
        {viewedItem && (
          <Section className="item-details">
            <div className="data-sec">
              <div className="left-bx">
                <div>
                  <span>Item brand & name</span>
                  <h2>
                    <i className="item-brand-letter">
                      {viewedItem.brand?.[0] || "I"}
                    </i>
                    {viewedItem.itemName}
                  </h2>
                </div>

                <div>
                  <span>Stock status</span>
                  <p>{viewedItem.inventory?.stockStatus || "N/A"}</p>
                </div>

                <div className="d-row">
                  {viewedItem.attributes?.size?.lens_diagonal && (
                    <div>
                      <span>Lens diagonal</span>
                      <p>{viewedItem.attributes.size.lens_diagonal}</p>
                    </div>
                  )}
                  {viewedItem.attributes?.size?.bridge && (
                    <div>
                      <span>Bridge</span>
                      <p>{viewedItem.attributes.size.bridge}</p>
                    </div>
                  )}
                  {viewedItem.attributes?.size?.arm_length && (
                    <div>
                      <span>Arm length</span>
                      <p>{viewedItem.attributes.size.arm_length}</p>
                    </div>
                  )}
                </div>

                {/* Other attributes mapping could be extended based on actual data structure */}
              </div>

              <div className="right-bx">
                <div>
                  <span>Item type</span>
                  <div>
                    <Badge
                      text={viewedItem.category || "ITEM"}
                      outlined
                      variant={makeBadgeVariant(viewedItem.category)}
                    />
                  </div>
                </div>

                {viewedItem.attributes?.model_number && (
                  <div>
                    <span>Model number</span>
                    <p>{viewedItem.attributes.model_number}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bot-box">
              <DataRow
                labelLeft="Purchase Price"
                dataLeft={
                  <div className="bot-box-item-detail-wrapper">
                    {currencyFormatter(viewedItem.purchasePrice)}
                    <small>LKR</small>
                  </div>
                }
                labelRight="Selling Price"
                dataRight={
                  <div className="bot-box-item-detail-wrapper">
                    {currencyFormatter(viewedItem.sellingPrice)}
                    <small>LKR</small>
                  </div>
                }
              />

              <DataRow
                labelLeft="Storeroom Stock"
                dataLeft={
                  <div className="bot-box-item-detail-wrapper">
                    {viewedItem.inventory?.storeRoomStock || 0}
                  </div>
                }
                labelRight="Showroom Stock"
                dataRight={
                  <div className="bot-box-item-detail-wrapper">
                    {viewedItem.inventory?.displayStock || 0}
                  </div>
                }
              />
            </div>
          </Section>
        )}
      </Modal>

      {mutation.isPending && <Loading variant="fullScreen" />}
    </>
  );
}

export default AddPurchasePage;
